import {
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  List,
  ListItem,
  Link,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'

// macOS traffic light button
const WindowButton = ({ color }) => (
  <Box w="12px" h="12px" borderRadius="full" bg={color} />
)

const PlainSection = ({ title, items }) => {
  const textColor = useColorModeValue('#282a36', '#f8f8f2')
  const headingColor = useColorModeValue('#6272a4', '#bd93f9')
  const labelColor = useColorModeValue('#6272a4', '#bd93f9')
  const valueColor = useColorModeValue('#282a36', '#8be9fd')
  const linkColor = useColorModeValue('#6272a4', '#50fa7b')

  return (
    <Box mb="6">
      <Heading
        as="h2"
        fontFamily="mono"
        fontSize={{ base: 'md', md: 'lg' }}
        fontWeight="600"
        color={headingColor}
        mb="3"
      >
        {title}
      </Heading>
      <List spacing="2" fontFamily="mono">
        {items.map((item, index) => (
          <ListItem key={index} fontSize="sm" color={textColor}>
            <Text as="span" color={labelColor} fontWeight="500">
              {item.label}:
            </Text>{' '}
            {item.link ? (
              <Link
                href={item.link}
                isExternal
                color={linkColor}
                _hover={{ textDecoration: 'underline' }}
              >
                {item.value}
              </Link>
            ) : (
              <Text as="span" color={valueColor}>
                {item.value}
              </Text>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

export default function Uses() {
  // Dracula theme colors
  const bgColor = useColorModeValue('#f8f8f2', '#282a36')
  const headerBg = useColorModeValue('#e8e8e8', '#44475a')
  const textColor = useColorModeValue('#282a36', '#f8f8f2')
  const promptColor = useColorModeValue('#6272a4', '#ff79c6')
  const keywordColor = useColorModeValue('#6272a4', '#8be9fd')
  const linkColor = useColorModeValue('#6272a4', '#50fa7b')
  const stringColor = useColorModeValue('#b58900', '#f1fa8c')
  const methodColor = useColorModeValue('#6272a4', '#50fa7b')
  const borderColor = useColorModeValue('#d1d1d1', '#44475a')

  const softwareTools = [
    { label: 'Editor', value: 'VS Code / IntelliJ' },
    { label: 'Terminal', value: 'iTerm2 + Tmux' },
    { label: 'Shell', value: 'Zsh' },
    { label: 'Browser', value: 'Firefox' },
    { label: 'Font', value: 'JetBrains Mono' },
    { label: 'Theme', value: 'Dracula' },
    { label: 'DB IDE', value: 'DataGrip / Dbeaver' },
    { label: 'Operating System', value: 'macOS & Linux' },
  ]

  const devTools = [
    { label: 'Version Control', value: 'Git + GitHub' },
    { label: 'API Testing', value: 'Postman / Insomnia' },
    { label: 'Containerization', value: 'Docker' },
    { label: 'Cloud', value: 'AWS' },
  ]

  const productivity = [
    { label: 'Notes', value: 'Obsidian' },
    { label: 'Password Manager', value: 'Bitwarden' },
    { label: 'Screenshots', value: 'CleanShot X / Lightshot' },
  ]

  return (
    <Flex flex="1">
      <Container maxW="60ch">
        <Stack
          as={Box}
          spacing={{ base: 6, md: 8 }}
          pt={{ base: '6rem', md: '8rem' }}
          pb={{ base: 30, md: '5rem' }}
          direction="column"
        >
          {/* Header */}
          <Box
            borderRadius="lg"
            overflow="hidden"
            boxShadow="lg"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex
              bg={headerBg}
              px="4"
              py="3"
              alignItems="center"
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              <HStack spacing="2" mr="4">
                <WindowButton color="#ff5f56" />
                <WindowButton color="#ffbd2e" />
                <WindowButton color="#27c93f" />
              </HStack>
              <Text
                fontFamily="mono"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
              >
                ~/uses
              </Text>
            </Flex>
            <Box bg={bgColor} p="4" fontFamily="mono">
              <Text color={textColor}>
                <Text as="span" color={promptColor}>
                  &gt;
                </Text>{' '}
                <Text as="span" color={keywordColor}>
                  const
                </Text>{' '}
                me ={' '}
                <Text as="span" color={keywordColor}>
                  Dev
                </Text>
                <Text as="span" color={methodColor}>
                  .get
                </Text>
                (
                <Text as="span" color={stringColor}>
                  'nemanja'
                </Text>
                );
              </Text>
              <Text color={textColor} mt="1">
                <Text as="span" color={promptColor}>
                  &gt;
                </Text>{' '}
                <Text as="span" color={keywordColor}>
                  console
                </Text>
                <Text as="span" color={methodColor}>
                  .log
                </Text>
                (me
                <Text as="span" color={methodColor}>
                  .uses
                </Text>
                ());
              </Text>
            </Box>
          </Box>

          {/* Intro */}
          <Text fontFamily="mono" fontSize="sm" color={textColor}>
            This page is inspired by{' '}
            <Link href="https://uses.tech/" isExternal color={linkColor}>
              uses.tech
            </Link>{' '}
            — below you can find the tools and software I use in my workflow.
          </Text>

          {/* Sections */}
          <PlainSection title="💿 Software & Tools" items={softwareTools} />
          <PlainSection title="🛠️ Development Tools" items={devTools} />
          <PlainSection title="📝 Productivity" items={productivity} />
        </Stack>
      </Container>
    </Flex>
  )
}
